import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bannerMovies } from '@/mocks/movies';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % bannerMovies.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const movie = bannerMovies[current];

  return (
    <section className="relative w-full h-[500px] md:h-[650px] lg:h-[700px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={movie.bannerUrl}
          alt={movie.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
      </div>

      <div className="absolute inset-0 flex items-end pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div
              key={current}
              className={`transition-all duration-600 ease-out ${
                isTransitioning ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
              }`}
            >
              <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/25 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-2 md:mb-3">
                {movie.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-300/80 max-w-2xl mb-2 md:mb-3">
                {movie.titleEn}
              </p>
              <div className="flex items-center gap-4 md:gap-6 text-xs md:text-sm text-foreground-400 mb-5 md:mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  <span className="text-accent-400 font-semibold">{movie.rating}</span>
                </span>
                <span>{movie.year}</span>
                <span>{movie.director}</span>
                <span className="hidden sm:inline">{movie.description}</span>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background-200/40 backdrop-blur-sm text-foreground-100 font-medium text-sm border border-background-300/30 hover:bg-background-200/60 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {bannerMovies.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-400 rounded-full cursor-pointer ${
              i === current
                ? 'w-8 h-2.5 bg-primary-500'
                : 'w-2.5 h-2.5 bg-foreground-400/40 hover:bg-foreground-300/60'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + bannerMovies.length) % bannerMovies.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-200/40 backdrop-blur-sm text-foreground-100 hover:bg-background-300/50 transition-all duration-300 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-200/40 backdrop-blur-sm text-foreground-100 hover:bg-background-300/50 transition-all duration-300 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
      </button>
    </section>
  );
}